// useImportacionFormXL - Sprint 44 Fase 3 Vista 10.
// Hook form de Importacion. Carga inicial + couriers + save + acciones
// (TRM today/viernes, distribuir costos, ver tracking). msg auto-dismiss 4s.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../api/apiClient';
import { EMPTY } from '../utils';

const MSG_AUTO_DISMISS_MS = 4000;

export default function useImportacionFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nueva';

  const [form, setForm]       = useState(EMPTY);
  const [couriers, setCouriers] = useState([]);
  const [tab, setTab]         = useState('basicos');
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [loaded, setLoaded]   = useState(false);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rC = await apiClient.get('/couriers/?activo=true');
        if (alive) setCouriers((rC && rC.data && rC.data.items) || []);
      } catch (_) { if (alive) setCouriers([]); }

      if (isNew) {
        try {
          const r = await apiClient.get('/importaciones/next-reference');
          if (alive) setForm((f) => ({
            ...f,
            referencia: (r && r.data && r.data.referencia) || '',
            fecha: new Date().toISOString().slice(0, 10),
          }));
        } catch (_) { /* ignore */ }
        if (alive) setLoaded(true);
      } else {
        try {
          const r = await apiClient.get('/importaciones/' + id);
          const d = (r && r.data) || {};
          if (alive) setForm({
            ...EMPTY, ...d,
            courier_id: d.courier_id || '',
            fecha: d.fecha || '',
            trm_financiera_fecha: d.trm_financiera_fecha || '',
            trm_aduanera_fecha: d.trm_aduanera_fecha || '',
            flete:  d.flete  == null ? '' : d.flete,
            aduana: d.aduana == null ? '' : d.aduana,
            broker: d.broker == null ? '' : d.broker,
            otros:  d.otros  == null ? '' : d.otros,
            trm_financiera: d.trm_financiera == null ? '' : d.trm_financiera,
            trm_aduanera:   d.trm_aduanera   == null ? '' : d.trm_aduanera,
            trm_manual:     d.trm_manual     == null ? '' : d.trm_manual,
            peso_kg:    d.peso_kg    == null ? '' : d.peso_kg,
            volumen_m3: d.volumen_m3 == null ? '' : d.volumen_m3,
            guia_aerea:       d.guia_aerea       || '',
            courier_tracking: d.courier_tracking || '',
          });
        } catch (e) {
          if (alive) pushMsg('err', 'No se pudo cargar la importacion');
        } finally {
          if (alive) setLoaded(true);
        }
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, pushMsg]);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const buildPayload = useCallback(() => {
    const p = { ...form };
    const numKeys = [
      'flete', 'aduana', 'broker', 'otros',
      'trm_financiera', 'trm_aduanera', 'trm_manual',
      'peso_kg', 'volumen_m3',
    ];
    numKeys.forEach((k) => { if (p[k] === '') p[k] = null; });
    const dateKeys = ['fecha', 'trm_financiera_fecha', 'trm_aduanera_fecha'];
    dateKeys.forEach((k) => { if (!p[k]) p[k] = null; });
    if (!p.courier_id)  p.courier_id  = null;
    if (!p.supplier_id) p.supplier_id = null;
    return p;
  }, [form]);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const r = await apiClient.post('/importaciones/', payload);
        const newId = r && r.data && r.data.id;
        pushMsg('ok', 'Importacion creada');
        if (newId) navigate('/importaciones/' + newId, { replace: true });
      } else {
        await apiClient.put('/importaciones/' + id, payload);
        pushMsg('ok', 'Importacion actualizada');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isNew, id, buildPayload, navigate, pushMsg]);

  const onVerTracking = useCallback(async () => {
    if (isNew) return;
    try {
      const r = await apiClient.get('/importaciones/' + id + '/tracking-url');
      const url = r && r.data && r.data.url;
      if (url) window.open(url, '_blank');
    } catch (e) { pushMsg('err', (e && e.message) || 'Error obteniendo URL'); }
  }, [isNew, id, pushMsg]);

  const onPdf = useCallback(() => {
    if (isNew) return;
    const base = apiClient.API_BASE || '/api';
    window.open(base + '/importaciones/' + id + '/pdf', '_blank');
  }, [isNew, id]);

  const onTrmToday = useCallback(async () => {
    try {
      const r = await apiClient.get('/importaciones/trm-today');
      const d = (r && r.data) || {};
      if (d.trm_usd) {
        set('trm_financiera', d.trm_usd);
        set('trm_financiera_fecha', d.fecha);
        set('trm_financiera_fuente', d.fuente || 'banrep');
      }
    } catch (_) { /* ignore */ }
  }, [set]);

  const onTrmViernes = useCallback(async () => {
    if (!form.fecha) {
      pushMsg('err', 'Ingresa la fecha de arribo primero (campo Fecha en Basicos)');
      return;
    }
    try {
      const r = await apiClient.get('/importaciones/trm-viernes-anterior?fecha_arribo=' + form.fecha);
      const d = (r && r.data) || {};
      if (d.trm_usd) {
        set('trm_aduanera', d.trm_usd);
        set('trm_aduanera_fecha', d.fecha_viernes);
        set('trm_aduanera_fuente', d.fuente || 'banrep');
      }
    } catch (_) { /* ignore */ }
  }, [form.fecha, set, pushMsg]);

  const onDistribuir = useCallback(async () => {
    if (isNew) return;
    if (!window.confirm('Distribuir costos sobre las lineas actuales?')) return;
    try {
      await apiClient.post('/importaciones/' + id + '/distribuir-costos', { metodo: 'proporcional_valor' });
      pushMsg('ok', 'Costos distribuidos');
      const r = await apiClient.get('/importaciones/' + id);
      const d = (r && r.data) || {};
      setForm((f) => ({ ...f, ...d }));
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error distribuyendo');
    }
  }, [isNew, id, pushMsg]);

  const courierSeleccionado = useMemo(
    () => couriers.find((c) => String(c.id) === String(form.courier_id)),
    [couriers, form.courier_id],
  );

  return {
    id, isNew, navigate,
    form, set, couriers, courierSeleccionado,
    tab, setTab,
    saving, msg, pushMsg, clearMsg: () => setMsg(null),
    loaded,
    onSave, onVerTracking, onPdf, onTrmToday, onTrmViernes, onDistribuir,
  };
}
